import type { Worker } from 'tesseract.js';

/**
 * Reading the printed names off a chart — as a suggestion, never as an answer.
 *
 * The objection to OCR here was never accuracy. It was authority. "SCHOUDER"
 * against "SCHOUDERKARBONADE" is a coin flip between two real cuts, and a
 * misread that assigns itself puts the wrong photograph on a class where nothing
 * downstream will ever contradict it.
 *
 * As a suggestion the objection disappears. The picture of the word stays on
 * screen beside the text, so a wrong read is visible in the same glance that
 * confirms a right one, and nothing is attached until a person says so. What it
 * removes is the typing, which is the boring part.
 *
 * OFFLINE BY CONSTRUCTION. tesseract.js fetches its wasm and language data from
 * a CDN by default, which is useless on a plant network with no route out. Both
 * are served from this application instead — see public/tessdata. The cost is
 * about 5 MB fetched once and then cached by the browser, and it is only fetched
 * at all when somebody actually drops a chart.
 */

let worker: Worker | null = null;
let starting: Promise<Worker> | null = null;

async function getWorker(onProgress?: (message: string) => void): Promise<Worker> {
  if (worker) return worker;
  if (starting) return starting;

  starting = (async () => {
    // Imported here rather than at module load: the wasm is megabytes, and a
    // page that never reads a chart should never pay for it.
    const { createWorker } = await import('tesseract.js');
    const w = await createWorker(['nld', 'eng'], 1, {
      /**
       * All three paths local, so this works on a plant network with no route
       * out. Leaving any of them unset sends tesseract.js to a CDN, and passing
       * one as `undefined` is worse than leaving it out: it overrides the
       * default with nothing, and the worker fails with "importScripts: the URL
       * 'undefined' is invalid" — which is what happened here.
       */
      langPath: '/tessdata',
      workerPath: '/tesseract/worker.min.js',
      corePath: '/tesseract',
      logger: (m: { status: string; progress: number }) => {
        if (onProgress && m.status) {
          onProgress(
            m.progress > 0 && m.progress < 1
              ? `${m.status} ${Math.round(m.progress * 100)}%`
              : m.status,
          );
        }
      },
    });
    worker = w;
    return w;
  })();

  return starting;
}

export interface ReadLabel {
  /** What Tesseract made of it. Never trusted, always shown. */
  text: string;
  /** 0-100 from the engine. Low means look harder, not "discard". */
  confidence: number;
}

/**
 * Read a set of label crops.
 *
 * Sequential rather than parallel: one worker, and a chart has a few dozen
 * labels at most. Spinning up four workers to save two seconds costs four copies
 * of a multi-megabyte language model in memory.
 */
export async function readLabels(
  crops: string[],
  onProgress?: (done: number, total: number, message: string) => void,
): Promise<ReadLabel[]> {
  if (crops.length === 0) return [];
  const w = await getWorker((m) => onProgress?.(0, crops.length, m));

  const out: ReadLabel[] = [];
  for (let i = 0; i < crops.length; i += 1) {
    try {
      const { data } = await w.recognize(crops[i]!);
      out.push({
        // Charts print cut names in capitals; collapsing whitespace and
        // stripping the stray marks OCR invents around short words makes the
        // suggestion usable without changing what it read.
        text: data.text.replace(/\s+/g, ' ').trim(),
        confidence: Math.round(data.confidence),
      });
    } catch {
      // One unreadable crop must not lose the other thirty.
      out.push({ text: '', confidence: 0 });
    }
    onProgress?.(i + 1, crops.length, 'reading');
  }
  return out;
}

export async function disposeReader(): Promise<void> {
  const w = worker;
  worker = null;
  starting = null;
  await w?.terminate();
}

/**
 * Match a read name against the classes that already exist.
 *
 * Deliberately conservative. A near-match is offered; a poor one is not offered
 * at all, because a wrong pre-selection that a tired operator accepts is worse
 * than no pre-selection — they would have had to look either way, and only one
 * of those two states makes them look.
 */
export function suggestClass<T extends { id: number; code: string; name: string }>(
  text: string,
  classes: T[],
): { klass: T; score: number } | null {
  const needle = normalise(text);
  if (needle.length < 3) return null;

  let best: { klass: T; score: number } | null = null;
  for (const k of classes) {
    const score = Math.max(similarity(needle, normalise(k.name)), similarity(needle, normalise(k.code)));
    if (!best || score > best.score) best = { klass: k, score };
  }
  // 0.62 is where "SCHOUDER" stops matching "SCHOUDERKARBONADE" more strongly
  // than it matches itself. Below it, say nothing.
  return best && best.score >= 0.62 ? best : null;
}

function normalise(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/** Dice coefficient over character bigrams: cheap, and forgiving of one bad letter. */
function similarity(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const bigrams = (s: string) => {
    const out = new Map<string, number>();
    for (let i = 0; i < s.length - 1; i += 1) {
      const g = s.slice(i, i + 2);
      out.set(g, (out.get(g) ?? 0) + 1);
    }
    return out;
  };
  const A = bigrams(a);
  const B = bigrams(b);
  let shared = 0;
  for (const [g, n] of A) shared += Math.min(n, B.get(g) ?? 0);
  const total = a.length - 1 + (b.length - 1);
  return total > 0 ? (2 * shared) / total : 0;
}
